import type { CityName } from "./us-dot-field";

/**
 * Everything the two hero panels pin to the dot field, in the units of the
 * Figma comp (a 700x884 panel). Marks are not tied to a city: the panel picks
 * a free seat at random each time one pops up, so both the place and the order
 * change on every cycle.
 */

export interface CompanyReview {
  company: string;
  /** Pre-broken so the card never has to measure a wrapped line. */
  quote: string[];
  name: string;
  title: string;
  logo: string;
  logoWidth: number;
  logoHeight: number;
  avatar: string;
  /** Biases the square headshot toward the face inside a 24px circle. */
  avatarPosition: string;
}

/**
 * Customer reviews as published on terac.com, one sentence lifted verbatim from
 * each. They replace the bare logo chips: a logo alone says who uses Terac, a
 * review says what they got out of it, which is what the researcher side is
 * arguing.
 */
export const COMPANY_REVIEWS: CompanyReview[] = [
  {
    company: "Maze",
    quote: [
      "Terac's AI-led screening and ability to",
      "source fresh participants in days helps us",
      "reach niche audiences that we struggled to",
      "reach previously.",
    ],
    name: "Guillaume Polge",
    title: "DIRECTOR OF PANEL PARTNERSHIPS",
    logo: "/company_logos/Maze.svg",
    logoWidth: 66,
    logoHeight: 12,
    avatar: "/testimonials/guillaume.jpg",
    avatarPosition: "50% 30%",
  },
  {
    company: "Knit",
    quote: ["They've demonstrated a clear focus on", "delivering value."],
    name: "DJ",
    title: "FIELDWORK MANAGER",
    logo: "/company_logos/knit.svg",
    logoWidth: 30,
    logoHeight: 13,
    avatar: "/testimonials/dhananjay.jpg",
    avatarPosition: "50% 22%",
  },
  {
    company: "Vetto",
    quote: [
      "They are fast, flexible, and easy to work",
      "with, adapting well to changing needs and",
      "priorities.",
    ],
    name: "Daniel Rodrigues",
    title: "CHRO",
    // The shipped Vetto mark is white, declared in a <style> block, so it is
    // invisible on this card. `Vetto-dark.svg` is the same art in panel ink.
    logo: "/company_logos/Vetto-dark.svg",
    logoWidth: 45,
    logoHeight: 13,
    avatar: "/testimonials/daniel.jpg",
    avatarPosition: "52% 26%",
  },
];

/**
 * The review card grows with its quote. Everything below the quote hangs off
 * the last line, so a four-line Maze quote and a two-line Knit one share one
 * set of numbers.
 */
export const REVIEW_CARD = {
  width: 328,
  quoteTop: 14,
  lineHeight: 16,
  /** Gap between the last quote line and the identity row. */
  gap: 14,
  avatar: 24,
  bottomPadding: 14,
};

export function reviewCardHeight(lines: number): number {
  return (
    REVIEW_CARD.quoteTop +
    lines * REVIEW_CARD.lineHeight +
    REVIEW_CARD.gap +
    REVIEW_CARD.avatar +
    REVIEW_CARD.bottomPadding
  );
}

export interface ExpertReview {
  name: string;
  /** Pre-broken so the card never has to measure a wrapped line. */
  quote: [string, string];
  role: string;
  statLabel: string;
  statValue: string;
  avatar: string;
  /** Biases the square headshot toward the face inside a 28px circle. */
  avatarPosition: string;
}

/**
 * Reviews as published on terac.com/experts. Each quote is one sentence lifted
 * verbatim from that testimonial, and each stat is that person's own figure,
 * which is why the labels differ.
 */
export const EXPERT_REVIEWS: ExpertReview[] = [
  {
    name: "Kritika Gupta",
    quote: [
      "It genuinely felt like my skills and",
      "experience were being valued.",
    ],
    role: "FINANCE & AI EVALUATION SPECIALIST",
    statLabel: "EARNED",
    statValue: "$8,500+",
    avatar: "/testimonials/kritika-gupta.jpg",
    avatarPosition: "44% 26%",
  },
  {
    name: "Nikita Annenkov",
    quote: [
      "The most interesting part so far has been",
      "how much the work rewards precision.",
    ],
    role: "ML SYSTEMS RESEARCHER",
    statLabel: "CADENCE",
    statValue: "Flexible async",
    avatar: "/testimonials/nikita-annenkov.jpg",
    avatarPosition: "50% 26%",
  },
  {
    name: "Laura Chen",
    quote: [
      "Terac involves you in projects that fit",
      "your experience closely.",
    ],
    role: "AI REVIEW ACCOUNTING EXPERT",
    statLabel: "CADENCE",
    statValue: "5-60 hr/wk",
    avatar: "/testimonials/laura-chen.jpg",
    avatarPosition: "52% 24%",
  },
  {
    name: "Megan Boyer",
    quote: [
      "The flexibility and community collaboration",
      "make every hour well worth my time.",
    ],
    role: "ACCOUNTING & AI TRAINING SPECIALIST",
    statLabel: "PROJECTS",
    statValue: "Multiple",
    avatar: "/testimonials/megan-boyer.jpg",
    avatarPosition: "50% 24%",
  },
];

/** Cities a mark may be seated on. Every one is a lit cell in this crop. */
export const SEAT_CITIES: CityName[] = [
  "Austin",
  "Houston",
  "StLouis",
  "Nashville",
  "Birmingham",
  "Atlanta",
  "Savannah",
  "Jacksonville",
  "Raleigh",
  "Philadelphia",
  "Richmond",
];

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Card and chip geometry, in comp units. */
export const CARD = { width: 344, height: 112 };
/**
 * Both testimonial cards are drawn this much larger than the comp sized them.
 * The comp's 11.5px quote is legible at a glance on a 1440 mock and not on a
 * real panel. Applied to the card's own scale rather than to the numbers
 * inside it, so the box, the padding, the avatar and the type all grow
 * together and a pre-broken quote line cannot outgrow the card it sits in.
 * The pin, pulse and leader stay at panel scale: they belong to the map.
 */
export const CARD_SCALE = 1.14;
/** How far a leader lifts a mark off its pin, and where it meets it. */
export const LEADER = { length: 24, inset: 18 };
/**
 * Clearance demanded around copy, the panel edge and the other mark, plus how
 * far apart two pins must be. Without the pin term two marks can take seats on
 * the same lattice cell (one above it, one below) and read as one dot wearing
 * two labels.
 */
export const CLEARANCE = { copy: 24, edge: 8, mark: 20, pinSeparation: 110 };
/**
 * At most this many marks on a panel at once. Both sides now carry a review
 * card rather than a chip, and two of those can only stack vertically and
 * crowd the panel; one at a time keeps the review readable and the map visible.
 */
export const MAX_VISIBLE: Record<"expert" | "researcher", number> = {
  expert: 1,
  researcher: 1,
};
